import React, { FC, useState, useEffect } from 'react';
import { BreakpointsInPx } from '../../../../../constants/breakpoints.constants';
import { iconSize } from '../../../../../constants/icon.constants';
import useMediaQuery from '../../../../../lib/custom-hooks/useMediaQuery';
import { THeroCardProps } from '../../types/overlayCarouselPopup.types';
import VideoPlayer from '../../../videoPlayer';
import Picture from '../../../picture';
import { TImageComponentProps } from '../../../picture/types/picture.types';
import TextComponentDecider from '../../../TextComponentDecider';
import { googleClickOpenVideoEvent } from '../../../../../utils/analytics';

const HeroCard: FC<THeroCardProps> = ({
  id,
  showOverlayTitleAndDescription = false,
  customTextTitle = false,
  customTextDescription = false,
  editorial,
  desktopRendition,
  tabletRendition,
  mobileRendition,
  desktopImage,
  mobileImage,
  desktopAlt,
  title,
  description,
  isVideo,
  videoUrl,
  activeCard,
  index,
  playCallback,
  pauseCallback,
  endCallback,
  moveLeft,
  moveRight,
  onClickPlayButton,
  richTextTitle = false,
  richTextDescription = false,
}) => {
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const [swipeStart, setSwipeStart] = useState(0);
  const [videoVisible, setVideoVisible] = useState(false);

  useEffect(() => {
    setSwipeStart(0);
  }, [activeCard, index]);

  const handleClickVideoPlay = (): void => {
    if (!isWide) return;

    onClickPlayButton && onClickPlayButton(index);

    googleClickOpenVideoEvent(title?.value, (index || 0) + 1);
  };

  const handleTouchStartVideoPlay = (event): void => {
    if (isWide) return;
    const value = event?.changedTouches?.[0]?.pageX;
    if (!value) return;
    setSwipeStart(value);
  };

  const handleTouchEndVideoPlay = (event): void => {
    if (isWide) return;
    const value = event?.changedTouches?.[0]?.pageX;
    if (!value) {
      setSwipeStart(0);
      return;
    }

    if (value < swipeStart && swipeStart - value > 20) {
      if (moveRight) moveRight();
    } else if (value > swipeStart && value - swipeStart > 20) {
      if (moveLeft) moveLeft();
    }
    setSwipeStart(0);
  };

  const handleTouchStart = (event): void => {
    const value = event?.changedTouches?.[0]?.pageX;
    if (!value) return;
    setSwipeStart(value);
  };

  const handleTouchEnd = (event): void => {
    const value = event?.changedTouches?.[0]?.pageX;
    if (!value) {
      setSwipeStart(0);
      return;
    }

    if (value < swipeStart && swipeStart - value > 20) {
      if (moveRight) moveRight();
    } else if (value > swipeStart && value - swipeStart > 20) {
      if (moveLeft) moveLeft();
    } else {
      setSwipeStart(0);
    }
  };

  const getPictureProps = (): TImageComponentProps => {
    if (editorial) {
      return {
        editorialImageProps: {
          id: id,
          desktopImage: desktopImage,
          mobileImage: mobileImage,
          desktopTransformation: 'Size1100',
          mobileTransformation: 'Size500',
          alt: desktopAlt,
          cssClassPictureEl: 'overlay-carousel-popup__picture',
          onClick: handleClickVideoPlay,
          onTouchStart: isVideo ? handleTouchStartVideoPlay : handleTouchStart,
          onTouchEnd: isVideo ? handleTouchEndVideoPlay : handleTouchEnd,
        },
      };
    } else {
      return {
        productImageProps: {
          desktopRendition: desktopRendition,
          tabletRendition: tabletRendition,
          mobileRendition: mobileRendition,
          alt: desktopAlt,
          pictureClassName: 'overlay-carousel-popup__picture',
          onClick: handleClickVideoPlay,
          onTouchStart: isVideo ? handleTouchStartVideoPlay : handleTouchStart,
          onTouchEnd: isVideo ? handleTouchEndVideoPlay : handleTouchEnd,
        },
      };
    }
  };

  const isRichTextTitle = richTextTitle ? 'RichText' : 'Text';
  const isRichTextDescription = richTextDescription ? 'RichText' : 'Text';

  return (
    <>
      {!videoVisible && showOverlayTitleAndDescription && isWide && (
        <div className="overlay-carousel-popup__focus-image__text-wrapper">
          <div className="overlay-carousel-popup__focus-image__text-title">
            {title && <TextComponentDecider type={customTextTitle ? 'CustomText' : isRichTextTitle} field={title} />}
          </div>
          <div className="overlay-carousel-popup__focus-image__text-description">
            {description && (
              <TextComponentDecider type={customTextDescription ? 'CustomText' : isRichTextDescription} field={description} />
            )}
          </div>
        </div>
      )}
      {isVideo ? (
        <VideoPlayer
          link={videoUrl}
          autoPlay={true}
          showThumbnail={true}
          muted={!isWide}
          playCallback={playCallback}
          pauseCallback={pauseCallback}
          endCallback={endCallback}
          activeCard={activeCard}
          playIconClickCallback={() => {
            setVideoVisible(true);
          }}
          iconWrapperClassName="overlay-carousel-popup__slide-video-play-button"
          desktopPlayIconSize={iconSize.xlg}
          mobilePlayIconSize={iconSize.dsk}
          {...getPictureProps()}
        />
      ) : (
        <Picture {...getPictureProps()} />
      )}
    </>
  );
};

export default React.memo(HeroCard);
